import {
  CLOCK_SYNC_INTERVAL_MS,
  CLOCK_SYNC_SAMPLE_COUNT,
  computeSample,
  estimateClockOffset,
  type ClockSample,
} from "@ict/shared";
import { EVENTS, type AppSocket } from "./SocketClient";

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * NTP-style clock synchronization: measures round trips to the server and
 * derives an offset so client `serverNow()` matches the server's clock
 * without ever trusting a single noisy sample. See CEREMONY_SYNC.md.
 */
export class ClockSync {
  private samples: ClockSample[] = [];
  private offset = 0;
  private rtt = 0;
  private intervalHandle: number | null = null;

  constructor(private readonly socket: AppSocket) {
    this.socket.on(EVENTS.CLOCK_PONG, (payload) => this.onPong(payload));
  }

  private onPong = (payload: { clientSendTime: number; serverTime: number }): void => {
    const t2 = Date.now();
    const sample = computeSample(payload.clientSendTime, payload.serverTime, t2);
    this.samples.push(sample);
    if (this.samples.length > 24) this.samples.shift();
    this.offset = estimateClockOffset(this.samples);
    this.rtt = sample.rtt;
  };

  private sendPing(): void {
    this.socket.emit(EVENTS.CLOCK_PING, {
      clientSendTime: Date.now(),
      reportedRtt: this.rtt || undefined,
    });
  }

  /** Fires several rapid pings up front so we have a good offset estimate before anything time-critical happens. */
  async warmup(count: number = CLOCK_SYNC_SAMPLE_COUNT): Promise<void> {
    for (let i = 0; i < count; i++) {
      this.sendPing();
      await delay(70);
    }
  }

  startBackgroundSync(): void {
    if (this.intervalHandle !== null) return;
    this.intervalHandle = window.setInterval(() => this.sendPing(), CLOCK_SYNC_INTERVAL_MS);
  }

  stop(): void {
    if (this.intervalHandle !== null) {
      window.clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
  }

  getOffset(): number {
    return this.offset;
  }

  getRtt(): number {
    return this.rtt;
  }

  /** Best estimate of the server's current clock time. */
  serverNow(): number {
    return Date.now() + this.offset;
  }
}
